import { createSelector } from '@reduxjs/toolkit'
import isNull from 'lodash/isNull'
import { all, call, put, select } from 'redux-saga/effects'

import { calculateNewDrawableLocations } from './calculateDrawableLocationsFromNewGroups'
import drawShapeByType from './drawShapeByType'
import { modifyMaterialBasedOnConflicts } from './insertEstimatedOptionOverlay'
import { unlinkGeometryFromFlagAction } from './unlinkGeometryFromFlag'
import { updateOpeningGroupsSuccess } from '../../../actions/drawable'
import { OpeningGroupAPI } from '../../../models/activeDrawable'
import { DRAWING_TYPES } from '../../../shared/constants/drawable-types'
import { RootState } from '../../../stores'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Label, Workspace } from '../../lib/toolBoxes/2D'
import {
    getDrawableSelectionState,
    updateDrawableSelection,
} from '../../lib/utils/functionality-bindings/addSelectFunctionalityToDrawable'
import addTooltipsToPath from '../../lib/utils/functionality-bindings/addTooltipsToPath'
import { selectCurrentEstimatedOptionOverlayChunkIds, setDrawableLocations } from '../../slices/2D'
import { selectActiveDocumentChunkIds } from '../../slices/documents'
import {
    GeometryGroup,
    GeometryVisibilityRecord,
    selectDrawableGroupsGeometries,
    selectGeometryVisibilityRecord,
} from '../../slices/geometry'
import { selectIsConflictResolutionInProgress } from '../../slices/masterSetPlan'
import { ToolsState } from '../../slices/tools'
import { IMUP2DDrawableLocation } from '../../types'

export interface RedrawShapeStoreState {
    areaOpacity: ToolsState['areaOpacityValue']
    lineOpacity: ToolsState['lineOpacityValue']
}

export const selectRedrawState = createSelector(
    ({ IMUP }: RootState): RedrawShapeStoreState => ({
        areaOpacity: IMUP.tools.areaOpacityValue,
        lineOpacity: IMUP.tools.lineOpacityValue,
    }),
    (state: RedrawShapeStoreState): RedrawShapeStoreState => state
)

export function* drawAndAnnotatePath(
    location: IMUP2DDrawableLocation,
    areaOpacity: number,
    lineOpacity: number,
    regionPaths: paper.Path[],
    workspaceTool: Workspace,
    activeDocumentChunkIds: number[],
    geometryVisibilityRecord?: GeometryVisibilityRecord,
    isConflictResolutionInProgress = false,
    createdDuringConflictResolution = false
) {
    if (activeDocumentChunkIds.includes(location.document_chunk_id)) {
        const shape: paper.Path = yield call(drawShapeByType, location, areaOpacity, lineOpacity, regionPaths)

        yield call(addTooltipsToPath, shape, workspaceTool)

        const item = shape.parent?.data.drawable_id === location.drawable_id ? shape.parent : shape

        if (isConflictResolutionInProgress && createdDuringConflictResolution) {
            // A material created after Save has no pre-existing conflict copy.
            // Preserve this provenance when redraws replace its Paper item.
            shape.data.createdDuringConflictResolution = true
            item.data.createdDuringConflictResolution = true
            const selection = yield select(getDrawableSelectionState)

            updateDrawableSelection(item, selection)
        }

        // Keep original base geometry for completion/cancel. Only conflict copies
        // and explicitly new materials may be edited during the saved-overlay phase.
        if (isConflictResolutionInProgress && !createdDuringConflictResolution) {
            // Labeled materials keep their metadata on a parent group. Hide the whole group
            // so its label stays hidden and its children can be restored when conflicts finish.
            item.visible = false
            item.data.selectable = false
            item.data.isMultiSelectable = false
        } else if (geometryVisibilityRecord && location.drawable_id) {
            shape.visible = geometryVisibilityRecord[location.drawable_id] ?? true
        }

        if (location.shapeType === DRAWING_TYPES.AREA) {
            yield call(workspaceTool.sendItemToBackBeforeRaster, shape)
        }
    }
}

const getDeletedOpeningIds = (originalGroup: OpeningGroupAPI, newGroup: OpeningGroupAPI): number[] =>
    (originalGroup.openings || []).reduce(
        (deletedOpeningId, { id }) =>
            (newGroup.openings || []).some((o) => o.id === id) ? deletedOpeningId : [...deletedOpeningId, id],
        [] as number[]
    )

export function* handleUpdateOpeningGroupsSuccess2D(
    action: ReturnType<typeof updateOpeningGroupsSuccess> & { payload: { createdDrawableIds?: number[] } }
) {
    if (action.payload.skipCanvasRedraw) {
        return
    }

    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (isNull(manager)) return

    const [workspaceTool, labelTool]: [Workspace, Label] = yield call(manager.getTools, [Workspace.NAME, Label.NAME])

    const {
        originalGroup,
        newGroup,
        newGroup: { openings: newGroupOpenings },
    } = action.payload.openingGroups

    const createdDrawableIds = new Set(action.payload.createdDrawableIds ?? [])

    // Remove the original paper items for the changed openings
    for (const opening of newGroupOpenings) {
        const items: paper.Item[] | null = yield call(workspaceTool.getItemsWithDrawableId, opening.id)

        if (items?.length) {
            if (items.some((item) => item.data.createdDuringConflictResolution)) createdDrawableIds.add(opening.id)
            yield all(items.map((item) => call([item, 'remove'])))
        }
    }

    // Now we have to get and redraw the new openings on the canvas
    const openingGroups: GeometryGroup[] = yield select(selectDrawableGroupsGeometries, true)
    const filteredOpeningGroups: GeometryGroup[] = yield openingGroups.filter(
        (group) => group.id === action.payload.openingGroups.newGroup.id
    )

    // get opening groups with updated openings and drawables, calculateNewDrawableLocations should receive only
    // new openings and redraw only them
    const filteredOpeningGroupsWithUpdatedOpenings: GeometryGroup[] = yield filteredOpeningGroups.map(
        (openingGroup) => {
            if (openingGroup.id === action.payload.openingGroups.newGroup.id) {
                // Preserve isActive and isVisible from existing openings when merging with API response
                const openingsWithActiveState = newGroupOpenings.map((newOpening) => {
                    const existingOpening = openingGroup.openings.find((o) => o.id === newOpening.id)

                    return {
                        ...newOpening,
                        isActive: existingOpening?.isActive ?? true,
                        isVisible: existingOpening?.isVisible ?? true,
                    }
                })

                return {
                    ...openingGroup,
                    drawablesPerTab: openingsWithActiveState,
                    openings: openingsWithActiveState,
                }
            }

            return openingGroup
        }
    )

    const {
        additionalDrawableLocations,
        newDrawableLocations,
    }: { additionalDrawableLocations: IMUP2DDrawableLocation[]; newDrawableLocations: IMUP2DDrawableLocation[] } =
        yield call(calculateNewDrawableLocations, filteredOpeningGroupsWithUpdatedOpenings)

    // We've updated drawable locations -- we need to save them in the store's 2D slice.
    yield put(setDrawableLocations(newDrawableLocations))

    const { areaOpacity, lineOpacity }: RedrawShapeStoreState = yield select(selectRedrawState)

    const regionPaths = workspaceTool.getAllRegionItems() as paper.Path[]

    const activeDocumentChunkIds: number[] = yield select(selectActiveDocumentChunkIds)

    const geometryVisibilityRecord: GeometryVisibilityRecord = yield select(selectGeometryVisibilityRecord)

    const isConflictResolutionInProgress: boolean = yield select(selectIsConflictResolutionInProgress)

    yield all(
        additionalDrawableLocations.map((location) =>
            call(
                drawAndAnnotatePath,
                location,
                areaOpacity,
                lineOpacity,
                regionPaths,
                workspaceTool,
                activeDocumentChunkIds,
                geometryVisibilityRecord,
                isConflictResolutionInProgress,
                createdDrawableIds.has(location.drawable_id)
            )
        )
    )

    const activeEstimatedOptionOverlayChunkIds: number[] = yield select(selectCurrentEstimatedOptionOverlayChunkIds)

    for (const overlayChunkId of activeEstimatedOptionOverlayChunkIds) {
        yield call(modifyMaterialBasedOnConflicts, workspaceTool, labelTool, overlayChunkId)
    }

    if (originalGroup && newGroupOpenings.length < originalGroup.openings.length) {
        yield put(unlinkGeometryFromFlagAction({ openingIds: getDeletedOpeningIds(originalGroup, newGroup) }))
    }
}
